import {
  DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX,
  LaserTimeOptions,
  ShootTimeBand,
  ShootTimeFormulaVersion,
} from "portal/utils/shootTime";
import { HOVER_HIT_WIDTH, ShootTimeXScale, ShootTimeYScale } from "./layout";
import {
  pointsToSvgPath,
  sampleBandPointGroups,
} from "./formulaSamplingCalculation";
import { Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useMemo } from "react";

const BOUNDARY_DOT_RADIUS = 4;

/** Visible stroke of the primary shoot-time curve. */
export const FORMULA_LINE_STROKE_WIDTH = 2;

/** Curve of computed laser time across the full radius domain. */
export interface ShootTimeFormulaLineProps {
  bands: readonly ShootTimeBand[];
  formulaVersion: ShootTimeFormulaVersion;
  maxRadiusMm: number;
  plotWidth: number;
  xScale: ShootTimeXScale;
  yScale: ShootTimeYScale;
}

export const ShootTimeFormulaLine: FunctionComponent<
  ShootTimeFormulaLineProps
> = ({ bands, formulaVersion, maxRadiusMm, plotWidth, xScale, yScale }) => {
  const { t } = useTranslation();
  const {
    palette: {
      background: { paper },
    },
  } = useTheme();

  const bandGroups = useMemo(() => {
    const options: LaserTimeOptions =
      formulaVersion === "legacy"
        ? { formulaVersion }
        : {
            formulaVersion,
            fineTuneIndex: DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX,
          };
    return sampleBandPointGroups(bands, maxRadiusMm, plotWidth, options);
  }, [bands, formulaVersion, maxRadiusMm, plotWidth]);

  const lineSegments = bandGroups.flatMap((points, bandIndex) => {
    const band = bands[bandIndex];
    const start = points[0];
    const end = points.at(-1);
    if (!band || !start || !end) {
      return [];
    }
    return [
      {
        color: band.color,
        key: `${band.bounds.lower}-${band.bounds.upper}`,
        label: t("views.categoryTemplates.shootTime.fineTuneCurveLabel", {
          value:
            formulaVersion === "legacy"
              ? band.formula.fineTuneMultiplier
              : DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX,
        }),
        path: pointsToSvgPath(points, xScale, yScale),
        start: { x: xScale(start.x), y: yScale(start.y) },
        end: { x: xScale(end.x), y: yScale(end.y) },
      },
    ];
  });

  // first circle and last circle get cut off by the plot edges -- so let's not show them
  const dots = lineSegments.flatMap((segment, index) => [
    ...(index === 0
      ? []
      : [{ ...segment.start, color: segment.color, key: `${segment.key}-in` }]),
    ...(index === lineSegments.length - 1
      ? []
      : [{ ...segment.end, color: segment.color, key: `${segment.key}-out` }]),
  ]);

  return (
    <>
      {lineSegments.map((segment) => (
        <Tooltip key={segment.key} arrow followCursor title={segment.label}>
          <g>
            <path
              d={segment.path}
              fill="none"
              stroke="transparent"
              strokeWidth={HOVER_HIT_WIDTH}
              pointerEvents="stroke"
            />
            <path
              d={segment.path}
              fill="none"
              stroke={segment.color}
              strokeWidth={FORMULA_LINE_STROKE_WIDTH}
              pointerEvents="none"
            />
          </g>
        </Tooltip>
      ))}
      {[...dots].reverse().map((dot) => (
        <g key={dot.key} style={{ color: dot.color }} pointerEvents="none">
          <BoundaryDot cx={dot.x} cy={dot.y} fill={paper} />
        </g>
      ))}
    </>
  );
};

const BoundaryDot: FunctionComponent<{
  cx: number;
  cy: number;
  fill: string;
}> = ({ cx, cy, fill }) => (
  <circle
    cx={cx}
    cy={cy}
    r={BOUNDARY_DOT_RADIUS}
    fill={fill}
    stroke="currentColor"
    strokeWidth={2}
  />
);
